import { useEffect, useState } from "react";
import {
  getRiderWallet,
  getRiderStats,
  createRiderVirtualAccount,
  deleteRiderVirtualAccount,
  simulateRiderVirtualAccountFunding,
  withdrawRiderWallet,
  topupRiderWallet,
  verifyRiderTopup
} from "../../api/client";
import { listenForRiderEvents } from "../../api/socket";
import RiderNav from "../../components/RiderNav";
import PayoutAccountForm from "../../components/PayoutAccountForm";
import RiderProfileStatus from "../../components/RiderProfileStatus";
import LoadingIndicator from "../../components/LoadingIndicator";

const TX_LABELS = {
  earning:          { icon: "▲", label: "Digital Order Earning",       credit: true  },
  wallet_funding:   { icon: "💳", label: "Virtual Account Transfer",    credit: true  },
  topup:            { icon: "💳", label: "Wallet Top-Up",               credit: true  },
  cod_offset:       { icon: "📉", label: "COD Debt Offset",             credit: false },
  "commission-debit":{ icon: "▼", label: "COD Commission Deduction",   credit: false },
  payout:           { icon: "🤝", label: "Payout to Bank",              credit: false },
  welcome_bonus:    { icon: "🎁", label: "Welcome Bonus",               credit: true  },
  cashback:         { icon: "💰", label: "Cashback Reward",             credit: true  },
};

// Payouts go out via the Transfer API and settle asynchronously.
const PAYOUT_STATUS_LABEL = {
  pending: "⏳ Processing",
  failed: "↩️ Failed — refunded",
  reversed: "↩️ Reversed — refunded"
};

function txMeta(type) {
  return TX_LABELS[type] || { icon: "•", label: type, credit: true };
}

export default function RiderWallet() {
  const [wallet, setWallet] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Virtual account
  const [vaBusy, setVaBusy] = useState(false);
  const [vaError, setVaError] = useState("");

  // Withdraw (specific or full)
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawMessage, setWithdrawMessage] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);

  // Top-up flow
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupGateway, setTopupGateway] = useState("paystack");
  const [topupBusy, setTopupBusy] = useState(false);
  const [topupError, setTopupError] = useState("");
  // After gateway redirect back (Paystack/Flutterwave inline):
  const [topupRef, setTopupRef] = useState(null); // pending verification
  const [verifyBusy, setVerifyBusy] = useState(false);

  const [liveAlert, setLiveAlert] = useState("");
  const [simBusy, setSimBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [wRes, sRes] = await Promise.all([getRiderWallet(), getRiderStats()]);
      setWallet(wRes.data);
      setStats(sRes.data);
    } catch {
      setError("Could not load rider wallet information.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Real-time socket updates for wallet funding and virtual account readiness
  useEffect(() => {
    const riderId = stats?._id || localStorage.getItem("riderId");
    if (!riderId) return;
    const cleanup = listenForRiderEvents(riderId, {
      onWalletUpdate: (data) => {
        load();
        const amt = Number(data?.amount || 0);
        setLiveAlert(`⚡ Wallet updated! ${amt > 0 ? `₦${amt.toLocaleString()} credited.` : "Balance adjusted."}`);
        setTimeout(() => setLiveAlert(""), 8000);
      },
      onVirtualAccountReady: (data) => {
        if (data?.virtualAccount) {
          setStats((s) => ({ ...s, virtualAccount: data.virtualAccount }));
          setLiveAlert("🏦 Dedicated virtual account is active and ready to receive funds.");
          setTimeout(() => setLiveAlert(""), 8000);
        }
      }
    });
    return cleanup;
  }, [stats?._id]);

  const netBalance = wallet?.netBalance ?? 0;
  const isNegative = netBalance < 0;

  // --- Virtual Account ---
  const handleCreateVirtualAccount = async () => {
    setVaBusy(true);
    setVaError("");
    try {
      const { data } = await createRiderVirtualAccount("paystack");
      setStats((s) => ({ ...s, virtualAccount: data.virtualAccount }));
    } catch (err) {
      setVaError(err.response?.data?.error || "Could not create your virtual account. Try again shortly.");
    } finally {
      setVaBusy(false);
    }
  };

  const handleDeleteVirtualAccount = async () => {
    if (!window.confirm("Are you sure you want to remove this virtual account?")) return;
    setVaBusy(true);
    setVaError("");
    try {
      await deleteRiderVirtualAccount();
      setStats((s) => ({ ...s, virtualAccount: { status: "not_created" } }));
      setLiveAlert("Virtual account removed successfully.");
      setTimeout(() => setLiveAlert(""), 6000);
    } catch (err) {
      setVaError(err.response?.data?.error || "Could not remove virtual account.");
    } finally {
      setVaBusy(false);
    }
  };

  const handleSimulateFunding = async (amount = 5000) => {
    setSimBusy(true);
    setVaError("");
    try {
      await simulateRiderVirtualAccountFunding(amount);
      await load();
      setLiveAlert(`⚡ Simulated bank transfer of ₦${amount.toLocaleString()} received and credited to wallet!`);
      setTimeout(() => setLiveAlert(""), 8000);
    } catch (err) {
      setVaError(err.response?.data?.error || "Could not simulate virtual account transfer.");
    } finally {
      setSimBusy(false);
    }
  };

  // --- Withdraw ---
  const handleWithdraw = async () => {
    setWithdrawBusy(true);
    setWithdrawMessage("");
    setError("");
    try {
      const specific = withdrawAmount ? Number(withdrawAmount) : undefined;
      const { data } = await withdrawRiderWallet(specific);
      setWithdrawMessage(
        data.transferResult?.status === "success" || data.transferResult?.isMocked
          ? `✓ ₦${data.netPaidOut.toLocaleString()} sent to your bank account.`
          : `Withdrawal recorded (₦${data.netPaidOut.toLocaleString()}) — bank transfer is processing.`
      );
      setWithdrawAmount("");
      setShowWithdrawForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Withdrawal failed.");
    } finally {
      setWithdrawBusy(false);
    }
  };

  // --- Top-up ---
  const handleTopupInitiate = async () => {
    const amount = Number(topupAmount);
    if (!amount || amount < 100) { setTopupError("Minimum top-up is ₦100."); return; }
    setTopupBusy(true);
    setTopupError("");
    try {
      const redirectUrl = window.location.href;
      const { data } = await topupRiderWallet(amount, topupGateway, redirectUrl);
      const targetUrl = data.checkoutUrl || data.authorizationUrl || data.link;
      if (targetUrl) {
        window.location.href = targetUrl;
      } else if (data.reference) {
        setTopupRef(data.reference);
        setShowTopupModal(false);
      }
    } catch (err) {
      setTopupError(err.response?.data?.error || "Could not initiate top-up. Try again.");
    } finally {
      setTopupBusy(false);
    }
  };

  // Verify a pending topup reference (returned from gateway redirect or inline)
  const handleVerifyTopup = async () => {
    if (!topupRef) return;
    setVerifyBusy(true);
    setError("");
    try {
      await verifyRiderTopup(topupRef, topupGateway);
      setTopupRef(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not verify top-up.");
    } finally {
      setVerifyBusy(false);
    }
  };

  // If returning from gateway redirect with a reference in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("reference") || params.get("trxref") || params.get("tx_ref");
    const gateway = params.get("trxref") ? "paystack" : params.get("tx_ref") ? "flutterwave" : (params.get("gateway") || "paystack");
    if (ref) {
      setTopupRef(ref);
      setTopupGateway(gateway);
      // Clean URL
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  // COD limit progress
  const codAmount = stats?.unremittedCashTotal || 0;
  const codCount  = stats?.unremittedCodCount  || 0;
  const codMaxAmt = stats?.codLimitMaxAmount   || 15000;
  const codMaxCnt = stats?.codLimitMaxCount    || 10;
  const codAmtPct = Math.min(100, Math.round((codAmount / codMaxAmt) * 100));
  const codCntPct = Math.min(100, Math.round((codCount  / codMaxCnt) * 100));
  const codNearLimit = codAmtPct >= 80 || codCntPct >= 80;
  const codAtLimit   = stats?.isBlocked && stats?.blockReason?.toLowerCase().includes("cod");

  return (
    <div className="rider-app">
      <RiderNav />
      <div className="page wide page-with-nav">

        <RiderProfileStatus />

        <div className="brand-header">
          <span className="brand-logo">💳</span>
          <span className="brand-name">Dispatcher Wallet &amp; Earnings</span>
          <p className="brand-tagline">Track earnings, COD commission debits, and manage payouts</p>
        </div>

        {liveAlert && (
          <div style={{ background: "rgba(34, 197, 94, 0.15)", border: "1px solid var(--success)", color: "var(--success-hover)", padding: "10px 16px", borderRadius: "8px", marginBottom: "16px", fontSize: "13px", textAlign: "center", fontWeight: 500 }}>
            {liveAlert}
          </div>
        )}

        {error && <p className="error">{error}</p>}

        {/* Pending top-up verification banner */}
        {topupRef && (
          <div style={{ background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.35)", borderRadius: "var(--radius-md)", padding: "14px 16px", marginBottom: "18px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "var(--warning)" }}>
              ⏳ Top-up payment detected — tap below to confirm and credit your wallet.
            </p>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--text-muted)" }}>Reference: {topupRef}</p>
            <button
              style={{ width: "auto", padding: "10px 20px", alignSelf: "flex-start" }}
              disabled={verifyBusy}
              onClick={handleVerifyTopup}
            >
              {verifyBusy ? "Verifying..." : "✅ Confirm Top-Up"}
            </button>
          </div>
        )}

        {loading ? (
          <LoadingIndicator label="Loading wallet..." />
        ) : (
          <>
            {/* ── NET BALANCE BANNER ── */}
            <div className="delivery-card" style={{ textAlign: "center", marginBottom: "20px" }}>
              <p className="hint" style={{ marginBottom: "4px" }}>Net Wallet Balance</p>
              <div style={{ fontSize: "38px", fontWeight: "800", color: isNegative ? "var(--danger)" : "var(--success)" }}>
                {isNegative ? `-₦${Math.abs(netBalance).toLocaleString()}` : `₦${netBalance.toLocaleString()}`}
              </div>

              {isNegative ? (
                <div style={{ marginTop: "10px", padding: "10px 16px", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "var(--radius-md)", color: "var(--danger-hover)", fontSize: "13px" }}>
                  ⚠️ <strong>Negative Balance:</strong> You owe <strong>₦{Math.abs(netBalance).toLocaleString()}</strong> in platform commission for COD deliveries. Commission is auto-deducted from future digital earnings.
                </div>
              ) : (
                <>
                  <p className="hint" style={{ marginTop: "6px" }}>Positive balance ready for payout.</p>
                  {withdrawMessage && <p className="hint" style={{ color: "var(--success)", marginTop: "6px" }}>{withdrawMessage}</p>}
                </>
              )}

              {/* Action buttons */}
              <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap", marginTop: "14px" }}>
                <button
                  onClick={() => { setShowTopupModal(true); setTopupError(""); }}
                  style={{ width: "auto", padding: "10px 22px" }}
                >
                  💳 Top Up Wallet
                </button>

                {!isNegative && netBalance > 0 && (
                  <button
                    className="success"
                    style={{ width: "auto", padding: "10px 22px" }}
                    disabled={withdrawBusy}
                    onClick={() => {
                      setShowWithdrawForm(v => !v);
                      if (!withdrawAmount && stats?.payoutAccount?.preferredAmount) {
                        setWithdrawAmount(String(stats.payoutAccount.preferredAmount));
                      }
                    }}
                  >
                    🏦 Withdraw
                  </button>
                )}
              </div>

              {/* Withdraw form (expanded) */}
              {showWithdrawForm && !isNegative && netBalance > 0 && (
                <div style={{ marginTop: "14px", padding: "16px", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", textAlign: "left" }}>
                  <label style={{ fontSize: "13px", color: "var(--text-muted)", display: "block", marginBottom: "6px" }}>
                    Amount to withdraw (enter your preferred amount or leave blank for full balance)
                  </label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <div style={{ position: "relative", flex: 1 }}>
                      <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: "14px" }}>₦</span>
                      <input
                        type="number"
                        min="100"
                        max={netBalance}
                        placeholder={stats?.payoutAccount?.preferredAmount ? `Preferred: ${stats.payoutAccount.preferredAmount.toLocaleString()}` : netBalance.toLocaleString()}
                        value={withdrawAmount}
                        onChange={e => setWithdrawAmount(e.target.value)}
                        style={{ paddingLeft: "28px", margin: 0 }}
                      />
                    </div>
                    <button
                      className="success"
                      style={{ width: "auto", padding: "10px 18px", marginTop: 0, whiteSpace: "nowrap" }}
                      disabled={withdrawBusy || (withdrawAmount && Number(withdrawAmount) > netBalance)}
                      onClick={handleWithdraw}
                    >
                      {withdrawBusy ? "Sending..." : "Confirm"}
                    </button>
                    <button
                      className="secondary"
                      style={{ width: "auto", padding: "10px 14px", marginTop: 0 }}
                      onClick={() => { setShowWithdrawForm(false); setWithdrawAmount(""); }}
                    >✕</button>
                  </div>

                  {/* Quick amount selection buttons */}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                    <button
                      type="button"
                      className="secondary"
                      style={{ width: "auto", padding: "4px 10px", fontSize: "12px", marginTop: 0 }}
                      onClick={() => setWithdrawAmount(String(netBalance))}
                    >
                      Max Available (₦{netBalance.toLocaleString()})
                    </button>
                    {stats?.payoutAccount?.preferredAmount && stats.payoutAccount.preferredAmount <= netBalance && (
                      <button
                        type="button"
                        className="secondary"
                        style={{ width: "auto", padding: "4px 10px", fontSize: "12px", marginTop: 0, borderColor: "var(--accent)" }}
                        onClick={() => setWithdrawAmount(String(stats.payoutAccount.preferredAmount))}
                      >
                        ⭐ Preferred (₦{stats.payoutAccount.preferredAmount.toLocaleString()})
                      </button>
                    )}
                  </div>

                  <p className="hint" style={{ fontSize: "11px", marginTop: "6px" }}>
                    Available: ₦{netBalance.toLocaleString()} — funds go directly to your registered payout bank account.
                  </p>
                </div>
              )}
            </div>

            {/* ── COD LIMIT STATUS ── */}
            {(codAmount > 0 || codCount > 0) && (
              <div className="delivery-card" style={{ marginBottom: "20px", border: codAtLimit ? "1px solid var(--danger)" : codNearLimit ? "1px solid rgba(234,179,8,0.5)" : "1px solid var(--border)" }}>
                <h3 style={{ marginBottom: "12px" }}>🧾 Cash-on-Delivery (COD) Limit</h3>
                {codAtLimit && (
                  <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginBottom: "12px", fontSize: "13px", color: "var(--danger-hover)" }}>
                    🚫 <strong>Account Suspended:</strong> You have reached the COD collection limit. Please top up your wallet to remit commission or contact support.
                  </div>
                )}
                {codNearLimit && !codAtLimit && (
                  <div style={{ background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.3)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginBottom: "12px", fontSize: "13px", color: "var(--warning)" }}>
                    ⚠️ <strong>Approaching Limit:</strong> Remit or top up soon to avoid account suspension.
                  </div>
                )}

                {/* Cash collected progress */}
                <div style={{ marginBottom: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>
                    <span>Cash collected unremitted</span>
                    <span><strong style={{ color: codAmtPct >= 80 ? "var(--danger)" : "var(--text-primary)" }}>₦{codAmount.toLocaleString()}</strong> / ₦{codMaxAmt.toLocaleString()}</span>
                  </div>
                  <div style={{ height: "6px", background: "rgba(255,255,255,0.08)", borderRadius: "3px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${codAmtPct}%`, background: codAmtPct >= 100 ? "var(--danger)" : codAmtPct >= 80 ? "var(--warning)" : "var(--success)", transition: "width 0.4s" }} />
                  </div>
                </div>

                {/* COD deliveries progress */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>
                    <span>COD deliveries completed</span>
                    <span><strong style={{ color: codCntPct >= 80 ? "var(--danger)" : "var(--text-primary)" }}>{codCount}</strong> / {codMaxCnt}</span>
                  </div>
                  <div style={{ height: "6px", background: "rgba(255,255,255,0.08)", borderRadius: "3px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${codCntPct}%`, background: codCntPct >= 100 ? "var(--danger)" : codCntPct >= 80 ? "var(--warning)" : "var(--success)", transition: "width 0.4s" }} />
                  </div>
                </div>

                <p className="hint" style={{ fontSize: "11px", marginTop: "8px" }}>
                  Limits reset automatically when you remit cash or the platform confirms receipt. Commission is deducted from wallet top-ups &amp; digital earnings.
                </p>
              </div>
            )}

            {/* ── VIRTUAL ACCOUNT ── */}
            <div className="delivery-card" style={{ marginBottom: "24px" }}>
              <h3>💳 Your Virtual Account</h3>
              {stats?.virtualAccount?.status === "active" ? (
                <>
                  <p className="hint" style={{ marginBottom: "12px" }}>
                    Share this account to receive payments directly into your wallet — any bank transfer here credits you automatically.
                  </p>
                  <div className="info-row">
                    <span className="label">Account Number</span>
                    <span className="value" style={{ fontSize: "18px", letterSpacing: "1px" }}>{stats.virtualAccount.accountNumber}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Bank</span>
                    <span className="value">{stats.virtualAccount.bankName}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Account Name</span>
                    <span className="value">{stats.virtualAccount.accountName}</span>
                  </div>
                  <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      disabled={vaBusy}
                      onClick={handleDeleteVirtualAccount}
                      style={{
                        background: "rgba(239, 68, 68, 0.12)",
                        color: "var(--danger-hover)",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                        width: "auto",
                        margin: 0
                      }}
                    >
                      {vaBusy ? "Removing..." : "🗑️ Remove Virtual Account"}
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      disabled={simBusy}
                      onClick={() => handleSimulateFunding(5000)}
                      style={{
                        fontSize: "12px",
                        padding: "8px 16px",
                        width: "auto",
                        margin: 0
                      }}
                    >
                      {simBusy ? "Simulating..." : "⚡ Test Transfer (₦5,000)"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="hint" style={{ marginBottom: "12px" }}>
                    Get your own dedicated bank account number — anyone can transfer money straight into it and it credits your wallet instantly.
                  </p>
                  {vaError && <p className="error">{vaError}</p>}
                  <button disabled={vaBusy} onClick={handleCreateVirtualAccount}>
                    {vaBusy ? "Creating..." : "Create My Virtual Account"}
                  </button>
                </>
              )}
            </div>

            {/* ── PAYOUT ACCOUNT ── */}
            <div style={{ marginBottom: "24px" }}>
              <PayoutAccountForm
                payoutAccount={stats?.payoutAccount}
                riderName={stats?.name}
                onUpdated={(payoutAccount) => setStats((s) => ({ ...s, payoutAccount }))}
                onSweepUpdated={(sweepSchedule, preferredAmount) =>
                  setStats((s) => ({
                    ...s,
                    payoutAccount: { ...s?.payoutAccount, sweepSchedule, preferredAmount }
                  }))
                }
              />
            </div>

            {/* ── STATS METRICS GRID ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "24px" }}>
              <div className="delivery-card" style={{ padding: "16px" }}>
                <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>Digital Earnings</span>
                <strong style={{ fontSize: "20px", color: "var(--text-accent)" }}>₦{(wallet?.earningsBalance || 0).toLocaleString()}</strong>
                <p className="hint" style={{ fontSize: "11px", marginTop: "2px" }}>Credited from card/wallet orders</p>
              </div>

              <div className="delivery-card" style={{ padding: "16px" }}>
                <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>COD Commission Debits</span>
                <strong style={{ fontSize: "20px", color: "var(--danger-hover)" }}>₦{(wallet?.cashOwedBalance || 0).toLocaleString()}</strong>
                <p className="hint" style={{ fontSize: "11px", marginTop: "2px" }}>Deducted from cash collected</p>
              </div>

              <div className="delivery-card" style={{ padding: "16px" }}>
                <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>Completed Deliveries</span>
                <strong style={{ fontSize: "20px", color: "var(--text-primary)" }}>{stats?.totalCompleted || 0}</strong>
                <p className="hint" style={{ fontSize: "11px", marginTop: "2px" }}>⭐ {stats?.rating?.toFixed(1) || "5.0"} Rating</p>
              </div>

              <div className="delivery-card" style={{ padding: "16px" }}>
                <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>Commission Rate</span>
                <strong style={{ fontSize: "20px", color: "var(--text-primary)" }}>{stats?.commissionPercent || 20}%</strong>
                <p className="hint" style={{ fontSize: "11px", marginTop: "2px" }}>Platform fee on COD orders</p>
              </div>
            </div>

            {/* ── TRANSACTION LEDGER ── */}
            <h3>Wallet Transaction Log</h3>
            {!wallet?.transactions || wallet.transactions.length === 0 ? (
              <div className="empty-state">
                <span className="empty-state-icon">📋</span>
                <p className="empty-state-text">No wallet transactions recorded yet.</p>
              </div>
            ) : (
              wallet.transactions.map((tx, idx) => {
                const meta = txMeta(tx.type);
                return (
                  <div key={tx._id || idx} className="history-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <strong style={{ color: meta.credit ? "var(--success)" : "var(--danger-hover)" }}>
                        {meta.icon} {meta.label}
                      </strong>
                      {tx.type === "payout" && PAYOUT_STATUS_LABEL[tx.status] && (
                        <span style={{ fontSize: "12px", marginLeft: "8px", color: "var(--text-muted)" }}>
                          {PAYOUT_STATUS_LABEL[tx.status]}
                        </span>
                      )}
                      <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                        {tx.order
                          ? `Order #${tx.order._id?.slice(-6).toUpperCase()} (${tx.order.paymentMethod?.toUpperCase()})`
                          : tx.reference ? `Ref: ${tx.reference}` : "General Transaction"}
                        {tx.createdAt && ` · ${new Date(tx.createdAt).toLocaleString()}`}
                      </p>
                    </div>
                    <strong style={{ fontSize: "16px", color: meta.credit ? "var(--success)" : "var(--danger-hover)", whiteSpace: "nowrap" }}>
                      {meta.credit ? "+" : "-"}₦{tx.amount?.toLocaleString()}
                    </strong>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      {/* ── TOP-UP MODAL ── */}
      {showTopupModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "28px 24px", width: "100%", maxWidth: "400px", boxShadow: "var(--shadow-lg)" }}>
            <h3 style={{ marginBottom: "6px" }}>💳 Top Up Wallet</h3>
            <p className="hint" style={{ marginBottom: "20px", fontSize: "13px" }}>
              Funds will be credited immediately and any outstanding COD commission deducted automatically.
            </p>

            {topupError && <p className="error" style={{ marginBottom: "12px" }}>{topupError}</p>}

            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "13px", color: "var(--text-muted)", display: "block", marginBottom: "6px" }}>Amount (₦)</label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: "14px" }}>₦</span>
                <input
                  type="number"
                  min="100"
                  placeholder="e.g. 5000"
                  value={topupAmount}
                  onChange={e => setTopupAmount(e.target.value)}
                  style={{ paddingLeft: "28px", margin: 0, width: "100%", boxSizing: "border-box" }}
                  autoFocus
                />
              </div>
            </div>

            {/* Quick amount buttons */}
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" }}>
              {[1000, 2000, 5000, 10000].map(amt => (
                <button
                  key={amt}
                  className="secondary"
                  style={{ width: "auto", padding: "6px 14px", marginTop: 0, fontSize: "13px" }}
                  onClick={() => setTopupAmount(String(amt))}
                >
                  ₦{amt.toLocaleString()}
                </button>
              ))}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontSize: "13px", color: "var(--text-muted)", display: "block", marginBottom: "8px" }}>Select Payment Gateway</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div
                  onClick={() => setTopupGateway("paystack")}
                  style={{
                    border: `2px solid ${topupGateway === "paystack" ? "var(--accent)" : "var(--border)"}`,
                    background: topupGateway === "paystack" ? "rgba(59,130,246,0.12)" : "rgba(255,255,255,0.02)",
                    borderRadius: "8px",
                    padding: "12px",
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.2s"
                  }}
                >
                  <div style={{ fontSize: "18px", marginBottom: "4px" }}>💳</div>
                  <strong style={{ fontSize: "14px", display: "block", color: "var(--text-primary)" }}>Paystack</strong>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>Card · Transfer · USSD</span>
                </div>

                <div
                  onClick={() => setTopupGateway("flutterwave")}
                  style={{
                    border: `2px solid ${topupGateway === "flutterwave" ? "var(--accent)" : "var(--border)"}`,
                    background: topupGateway === "flutterwave" ? "rgba(59,130,246,0.12)" : "rgba(255,255,255,0.02)",
                    borderRadius: "8px",
                    padding: "12px",
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.2s"
                  }}
                >
                  <div style={{ fontSize: "18px", marginBottom: "4px" }}>🦋</div>
                  <strong style={{ fontSize: "14px", display: "block", color: "var(--text-primary)" }}>Flutterwave</strong>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>Card · Bank · OPay</span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                style={{ flex: 1 }}
                disabled={topupBusy || !topupAmount || Number(topupAmount) < 100}
                onClick={handleTopupInitiate}
              >
                {topupBusy ? "Initiating..." : "Proceed to Payment"}
              </button>
              <button
                className="secondary"
                style={{ flex: 1 }}
                onClick={() => { setShowTopupModal(false); setTopupAmount(""); setTopupError(""); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
